import {
  init,
  backButton,
  isTMA,
  isFullscreen,
  retrieveLaunchParams,
  viewport,
} from "@telegram-apps/sdk";
import { useEffect } from "react";
import { GetUrlByGuidQuery } from "arena/queries";
import { useQuery } from "@apollo/client";
import { setUtmToLocalStorage } from "core/utils/utm";

export const isTelegramMiniApp = isTMA("simple");
export const isTelegramMiniAppOnFullscreen =
  isTelegramMiniApp && isFullscreen();

export const launchParams = isTelegramMiniApp ? retrieveLaunchParams() : null;

export const webAppVersion = launchParams?.version || "6.0";

// --- Event system (based on official telegram-web-app.js) ---

const eventHandlers = {};

export function onEvent(eventType, callback) {
  if (!eventHandlers[eventType]) {
    eventHandlers[eventType] = [];
  }
  if (eventHandlers[eventType].indexOf(callback) === -1) {
    eventHandlers[eventType].push(callback);
  }
}

export function offEvent(eventType, callback) {
  if (!eventHandlers[eventType]) return;
  const index = eventHandlers[eventType].indexOf(callback);
  if (index !== -1) {
    eventHandlers[eventType].splice(index, 1);
  }
}

function callEventCallbacks(eventType, eventData) {
  const handlers = eventHandlers[eventType];
  if (!handlers || !handlers.length) return;
  for (let i = 0; i < handlers.length; i++) {
    try {
      handlers[i](eventData);
    } catch (e) {
      console.error("[Telegram] Event callback error:", e);
    }
  }
}

// --- postEvent (based on official telegram-web-app.js) ---

const isBrowser = typeof window !== "undefined";

let isIframe = false;
if (isBrowser) {
  try {
    isIframe = window.parent !== null && window !== window.parent;
  } catch (e) {
    // ignore
  }
}

function customPostEvent(eventType, eventData) {
  if (!isBrowser) return;
  if (eventData === undefined) eventData = {};

  if (window.TelegramWebviewProxy !== undefined) {
    window.TelegramWebviewProxy.postEvent(eventType, JSON.stringify(eventData));
  } else if (window.external && "notify" in window.external) {
    window.external.notify(JSON.stringify({ eventType, eventData }));
  } else if (isIframe) {
    try {
      window.parent.postMessage(JSON.stringify({ eventType, eventData }), "*");
    } catch (e) {
      // ignore
    }
  }
}

// --- Fullscreen state ---

let webAppIsFullscreen = false;

function receiveEvent(eventType, eventData) {
  if (eventType === "fullscreen_changed") {
    webAppIsFullscreen = !!eventData.is_fullscreen;
    callEventCallbacks("fullscreen_changed", eventData);
  } else if (eventType === "fullscreen_failed") {
    if (eventData.error === "ALREADY_FULLSCREEN" && !webAppIsFullscreen) {
      webAppIsFullscreen = true;
    }
    callEventCallbacks("fullscreen_failed", eventData);
  }
}

export function isAppFullscreen() {
  if (!isBrowser) return false;
  return webAppIsFullscreen;
}

export function requestFullscreen() {
  if (!isTelegramMiniApp || !versionAtLeast("8.0")) return false;
  customPostEvent("web_app_request_fullscreen");
  return true;
}

export function exitFullscreen() {
  if (!isTelegramMiniApp || !versionAtLeast("8.0")) return false;
  customPostEvent("web_app_exit_fullscreen");
  return true;
}

// --- Init ---

if (isTelegramMiniApp) {
  customPostEvent("web_app_ready");

  init({ acceptCustomStyles: false });

  console.log("launchParams", retrieveLaunchParams());

  customPostEvent("web_app_expand");
  customPostEvent("web_app_setup_closing_behavior", {
    need_confirmation: false, // eslint-disable-line camelcase
  });
  customPostEvent("web_app_setup_swipe_behavior", {
    allow_vertical_swipe: false, // eslint-disable-line camelcase
  });

  if (viewport.mount.isAvailable()) {
    viewport.mount();
  }

  // Перехватываем события от нативного клиента Telegram.
  // На tdesktop нативный клиент вызывает напрямую:
  //   window.TelegramGameProxy.receiveEvent(eventType, eventData)
  // SDK при init() ставит свой обработчик. Мы оборачиваем его,
  // добавляя свою логику (fullscreen), и прокидываем дальше в SDK.
  const sdkReceiveEvent = window.TelegramGameProxy?.receiveEvent;

  const wrappedReceiveEvent = function (eventType, eventData) {
    receiveEvent(eventType, eventData);
    if (sdkReceiveEvent) sdkReceiveEvent(eventType, eventData);
  };

  window.TelegramGameProxy = { receiveEvent: wrappedReceiveEvent };
  window.TelegramGameProxy_receiveEvent = wrappedReceiveEvent; // eslint-disable-line camelcase

  // Для iframe (web.telegram.org) - события через postMessage
  if (isIframe) {
    window.addEventListener("message", function (event) {
      if (event.source !== window.parent) return;
      try {
        const data = JSON.parse(event.data);
        if (data?.eventType) receiveEvent(data.eventType, data.eventData);
      } catch (e) {
        // ignore
      }
    });
  }
}

// --- Utils ---

export function openLink(_url, options) {
  var a = document.createElement("a");
  a.href = _url;
  options = options || {};

  const url = a.href;

  if (versionAtLeast("6.1")) {
    let reqParams = { url: url };
    if (versionAtLeast("6.4") && options.try_instant_view) {
      reqParams.try_instant_view = true; // eslint-disable-line camelcase
    }
    if (versionAtLeast("7.6") && options.try_browser) {
      reqParams.try_browser = options.try_browser; // eslint-disable-line camelcase
    }
    customPostEvent("web_app_open_link", reqParams);
  } else {
    window.open(url, "_blank");
  }
}

function versionAtLeast(ver) {
  return versionCompare(webAppVersion, ver) >= 0;
}

function versionCompare(v1, v2) {
  if (typeof v1 !== "string") v1 = "";
  if (typeof v2 !== "string") v2 = "";
  v1 = v1.replace(/^\s+|\s+$/g, "").split(".");
  v2 = v2.replace(/^\s+|\s+$/g, "").split(".");
  var a = Math.max(v1.length, v2.length),
    i,
    p1,
    p2;
  for (i = 0; i < a; i++) {
    p1 = parseInt(v1[i]) || 0;
    p2 = parseInt(v2[i]) || 0;
    if (p1 === p2) continue;
    if (p1 > p2) return 1;
    return -1;
  }
  return 0;
}

// --- Back button / fullscreen integration ---

let isPlayerFullscreenGlobal = false;
let exitFullscreenCallback = null;

export function setPlayerFullscreenState(value, exitCallback) {
  isPlayerFullscreenGlobal = value;
  exitFullscreenCallback = exitCallback;
}

export function useReactRouterIntegration(location, navigate) {
  useEffect(() => {
    if (!isTelegramMiniApp) return;

    customPostEvent("web_app_setup_back_button", {
      is_visible: location.pathname !== "/", // eslint-disable-line camelcase
    });

    if (backButton.onClick.isAvailable()) {
      const off = backButton.onClick(() => {
        // Если плеер в fullscreen - выходим из fullscreen вместо навигации
        if (isPlayerFullscreenGlobal && exitFullscreenCallback) {
          exitFullscreenCallback();
          return;
        }

        off();
        navigate(-1);
      });

      return off;
    }
  }, [location, navigate]);

  useEffect(() => {
    if (!isTelegramMiniApp) return;

    customPostEvent("web_app_setup_title", { title: document.title }); // eslint-disable-line camelcase
  }, [location]);

  let startParam;
  if (isTelegramMiniApp) {
    const launchParams = retrieveLaunchParams();
    startParam = launchParams?.startParam;
  }

  useQuery(GetUrlByGuidQuery, {
    skip: !isTelegramMiniApp || !startParam,
    variables: { guid: startParam },
    onCompleted: (data) => {
      if (data?.getUrlByGuid) {
        const resolvedUrl = data.getUrlByGuid;
        try {
          const parsed = new URL(resolvedUrl, window.location.origin);
          if (parsed.origin === window.location.origin) {
            navigate(parsed.pathname + (parsed.search || ""));
          }
        } catch (e) {
          navigate(resolvedUrl);
        }
        setUtmToLocalStorage(new URL(resolvedUrl, window.location.origin).href);
      }
    },
  });
}
